let recommendedProductsSwiperScriptPromise;
const prioritizedDrawerImageUrls = new Set();

function deferCartDrawerSecondaryWork(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 1200 });
    return;
  }

  window.requestAnimationFrame(() => window.setTimeout(callback, 0));
}

function escapeAttributeValue(value) {
  const stringValue = String(value ?? '');

  if (window.CSS?.escape) {
    return window.CSS.escape(stringValue);
  }

  return stringValue.replace(/["\\]/g, '\\$&');
}

function warmImageUrl(url) {
  if (!url || prioritizedDrawerImageUrls.has(url)) {
    return;
  }

  prioritizedDrawerImageUrls.add(url);

  const image = new Image();
  image.decoding = 'async';

  try {
    image.fetchPriority = 'high';
  } catch (_) {}

  image.src = url;
}

function prioritizeDrawerImages(root = document, variantId = null) {
  const scope = root instanceof Element ? root : document;
  const images = Array.from(scope.querySelectorAll('.cart-item__image'));

  if (!images.length) {
    return;
  }

  let prioritizedImage = null;

  if (variantId != null) {
    const matchingItem = scope.querySelector(
      `.cart-item[data-cart-variant-id="${escapeAttributeValue(variantId)}"]`
    );
    prioritizedImage = matchingItem?.querySelector('.cart-item__image') || null;
  }

  const imagesToPrioritize = [];

  if (prioritizedImage) {
    imagesToPrioritize.push(prioritizedImage);
  }

  images.slice(0, 3).forEach((image) => {
    if (!imagesToPrioritize.includes(image)) {
      imagesToPrioritize.push(image);
    }
  });

  imagesToPrioritize.forEach((image) => {
    image.loading = 'eager';
    image.decoding = 'async';
    image.setAttribute('loading', 'eager');
    image.setAttribute('decoding', 'async');

    try {
      image.fetchPriority = 'high';
    } catch (_) {}

    image.setAttribute('fetchpriority', 'high');
    warmImageUrl(image.currentSrc || image.src);
  });
}

function loadRecommendedProductsSwiper(root = document) {
  const drawer =
    root instanceof Element
      ? root.closest('cart-drawer') || root.querySelector?.('cart-drawer')
      : document.querySelector('cart-drawer');

  const carousel = drawer?.querySelector('.recommended-carousel');
  const scriptSrc = drawer?.dataset.recommendedProductsSwiperSrc;

  if (!carousel || !scriptSrc) {
    return Promise.resolve(false);
  }

  if (window.HFRecommendedProductsSwiper?.initAll) {
    window.HFRecommendedProductsSwiper.initAll();
    return Promise.resolve(true);
  }

  if (recommendedProductsSwiperScriptPromise) {
    return recommendedProductsSwiperScriptPromise;
  }

  const existingScript = document.querySelector('script[data-hf-recommended-products-slider]');

  if (existingScript) {
    recommendedProductsSwiperScriptPromise = Promise.resolve(true).then(() => {
      document.dispatchEvent(new CustomEvent('recommendedProductsRendered', { bubbles: true }));
      return true;
    });

    return recommendedProductsSwiperScriptPromise;
  }

  recommendedProductsSwiperScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.defer = true;
    script.dataset.hfRecommendedProductsSlider = 'true';
    script.onload = () => {
      document.dispatchEvent(new CustomEvent('recommendedProductsRendered', { bubbles: true }));
      resolve(true);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return recommendedProductsSwiperScriptPromise;
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.bindOverlayClick();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClick() {
    if (!this.overlayCloseHandler) {
      this.overlayCloseHandler = this.close.bind(this);
    }

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerBound === 'true') return;

    overlay.addEventListener('click', this.overlayCloseHandler);
    overlay.dataset.cartDrawerBound = 'true';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const detailsSummary = this.querySelector('[id^="Details-"] summary');
    if (detailsSummary && !detailsSummary.hasAttribute('role')) {
      this.setSummaryAccessibility(detailsSummary);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const focusTarget = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const fallbackTarget = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        trapFocus(focusTarget, fallbackTarget);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    deferCartDrawerSecondaryWork(() => loadRecommendedProductsSwiper(this));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');

    if (summary.nextElementSibling.getAttribute('id')) {
      summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    summary.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  replaceDrawerContent(nextDrawer) {
    const nextDrawerRoot = nextDrawer?.querySelector('#CartDrawer');
    const currentDrawerRoot = this.querySelector('#CartDrawer');

    if (!nextDrawer || !nextDrawerRoot || !currentDrawerRoot) {
      return false;
    }

    currentDrawerRoot.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      const href = link.getAttribute('href');
      if (href && !document.head.querySelector(`link[rel="stylesheet"][href="${CSS.escape(href)}"]`)) {
        document.head.appendChild(link);
      }
    });
    nextDrawerRoot.querySelectorAll('link[rel="stylesheet"]').forEach(link => link.remove());

    prioritizeDrawerImages(nextDrawerRoot, this.productId);
    currentDrawerRoot.replaceWith(nextDrawerRoot);

    if (window.customElements?.upgrade) {
      window.customElements.upgrade(this);
    }

    this.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    this.bindOverlayClick();
    prioritizeDrawerImages(this, this.productId);
    this.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    deferCartDrawerSecondaryWork(() => {
      document.dispatchEvent(new CustomEvent('recommendedProductsRendered', { bubbles: true }));
      loadRecommendedProductsSwiper(this);
      window.CartCountdown?.scheduleRefreshAll?.();
    });

    return true;
  }

  renderSectionMarkup(html, selector = 'cart-drawer') {
    if (!html) return false;

    const nextDrawer = this.getSectionDOM(html, selector);
    return this.replaceDrawerContent(nextDrawer);
  }

  async refreshFromPage() {
    const response = await fetch(window.location.pathname, {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Cart drawer refresh failed: ${response.status}`);
    }

    const markup = await response.text();
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const nextDrawer = parsedDocument.querySelector('cart-drawer');
    return this.replaceDrawerContent(nextDrawer);
  }

  async renderContents(parsedState) {
    await this.refreshContents(parsedState);
    this.open();
  }

  async refreshContents(parsedState) {
    this.productId = parsedState.id;

    const iconBubble = document.getElementById('cart-icon-bubble');
    if (iconBubble && parsedState.sections?.['cart-icon-bubble']) {
      iconBubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
    }

    const hasItems = typeof parsedState.item_count === 'number' ? parsedState.item_count > 0 : true;

    const drawerMarkup =
      parsedState.sections?.['cart-drawer-group'] || parsedState.sections?.['cart-drawer'];
    let refreshed = false;

    try {
      if (drawerMarkup) {
        refreshed = this.renderSectionMarkup(drawerMarkup);
      }

      if (!refreshed) {
        refreshed = await this.refreshFromPage();
      }
    } catch (error) {
      console.error('Cart drawer refresh error:', error);
    }

    if (!refreshed) {
      this.hidePendingState();
    }

    this.classList.toggle('is-empty', !hasItems);

    document.querySelectorAll(".cdt-root[data-cart-empty='true']").forEach((element) => {
      element.dataset.cartEmpty = 'false';
      delete element.dataset.initialized;
      element.hidden = false;
    });

    document.dispatchEvent(new CustomEvent('cart:item:added', { bubbles: true }));
  }

  showPendingState() {
    const contents = this.querySelector('#CartDrawer-CartItems');
    if (!contents) return false;

    contents.querySelectorAll('[data-hf-cart-pending]').forEach((node) => node.remove());

    const pending = document.createElement('div');
    pending.className = 'cart-drawer__pending';
    pending.dataset.hfCartPending = 'true';
    pending.setAttribute('role', 'status');
    pending.setAttribute('aria-live', 'polite');
    pending.innerHTML = '<span class="cart-drawer__pending-spinner" aria-hidden="true"></span><span class="visually-hidden">Chargement du panier</span>';

    contents.insertBefore(pending, contents.firstChild);
    this.classList.remove('is-empty');
    contents.closest('cart-drawer-items')?.classList.remove('is-empty');
    this.open();

    return true;
  }

  hidePendingState() {
    this.querySelectorAll('[data-hf-cart-pending]').forEach((node) => node.remove());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer-group', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer-group', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
